import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiFileText,
  FiLink,
  FiEdit3,
  FiCode,
  FiDatabase,
  FiFolder,
  FiMapPin,
  FiExternalLink,
  FiEdit2,
  FiTrash2,
  FiMoreVertical,
  FiClock,
  FiTag,
  FiBookOpen,
  FiAlertTriangle,
  FiEye,
} from "react-icons/fi";
import { timeAgo } from "../../../../utils/fileUtils";
import CodeViewerModal from "./CodeViewerModal";
import NoteViewerModal from "./NoteViewerModal";
import CitationViewerModal from "./CitationViewerModal";
import "./LibraryResourceCard.css";

// ============================================
// CONSTANTS
// ============================================
const RESOURCE_ICONS = {
  paper: FiFileText,
  link: FiLink,
  note: FiEdit3,
  file: FiFileText,
  code: FiCode,
  dataset: FiDatabase,
  reference: FiBookOpen,
  other: FiFolder,
};

const RESOURCE_COLORS = {
  paper: {
    bg: "rgba(59, 130, 246, 0.1)",
    color: "#3b82f6",
    shadow: "rgba(59, 130, 246, 0.25)",
  },
  link: {
    bg: "rgba(14, 165, 233, 0.1)",
    color: "#0ea5e9",
    shadow: "rgba(14, 165, 233, 0.25)",
  },
  note: {
    bg: "rgba(16, 185, 129, 0.1)",
    color: "#10b981",
    shadow: "rgba(16, 185, 129, 0.25)",
  },
  code: {
    bg: "rgba(245, 158, 11, 0.1)",
    color: "#f59e0b",
    shadow: "rgba(245, 158, 11, 0.25)",
  },
  dataset: {
    bg: "rgba(139, 92, 246, 0.1)",
    color: "#8b5cf6",
    shadow: "rgba(139, 92, 246, 0.25)",
  },
  file: {
    bg: "rgba(100, 116, 139, 0.1)",
    color: "#64748b",
    shadow: "rgba(100, 116, 139, 0.25)",
  },
  reference: {
    bg: "rgba(236, 72, 153, 0.1)",
    color: "#ec4899",
    shadow: "rgba(236, 72, 153, 0.25)",
  },
  other: {
    bg: "rgba(100, 116, 139, 0.1)",
    color: "#64748b",
    shadow: "rgba(100, 116, 139, 0.25)",
  },
};

const PRIORITY_CONFIG = {
  low: { color: "#10b981", bg: "rgba(16, 185, 129, 0.1)", label: "Low" },
  normal: { color: "#3b82f6", bg: "rgba(59, 130, 246, 0.1)", label: "Normal" },
  high: { color: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)", label: "High" },
  critical: { color: "#ef4444", bg: "rgba(239, 68, 68, 0.1)", label: "Critical", icon: FiAlertTriangle },
};

// ============================================
// SUB-COMPONENTS
// ============================================

const ResourceIcon = ({ type }) => {
  const IconComponent = RESOURCE_ICONS[type] || RESOURCE_ICONS.other;
  const colors = RESOURCE_COLORS[type] || RESOURCE_COLORS.other;

  return (
    <div
      className="rlcard-icon"
      style={{ background: colors.bg, color: colors.color }}
    >
      <IconComponent size={22} />
    </div>
  );
};

const PriorityBadge = ({ priority }) => {
  if (!priority || priority === "normal") return null;
  
  const config = PRIORITY_CONFIG[priority];
  if (!config) return null;
  
  const Icon = config.icon;
  
  return (
    <span 
      className="rlcard-priority-badge"
      style={{ background: config.bg, color: config.color }}
    >
      {Icon && <Icon size={10} />}
      {config.label}
    </span>
  );
};

const ActionMenu = ({ onEdit, onDelete, canEdit, canDelete, onClose }) => (
  <motion.div
    className="rlcard-menu"
    initial={{ opacity: 0, scale: 0.9, y: -10 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.9, y: -10 }}
    transition={{ duration: 0.15 }}
  >
    {canEdit && (
      <button
        className="rlcard-menu-item"
        onClick={() => {
          onEdit();
          onClose();
        }}
      >
        <FiEdit2 size={14} />
        <span>Edit</span>
      </button>
    )}
    {canDelete && (
      <button
        className="rlcard-menu-item rlcard-menu-item--danger"
        onClick={() => {
          onDelete();
          onClose();
        }}
      >
        <FiTrash2 size={14} />
        <span>Delete</span>
      </button>
    )}
  </motion.div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const LibraryResourceCard = ({
  resource,
  viewMode = "grid",
  userRole,
  currentUserId,
  onUpdate,
  onDelete,
  onTogglePin,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showCodeViewer, setShowCodeViewer] = useState(false);
  const [showNoteViewer, setShowNoteViewer] = useState(false);
  const [showCitationViewer, setShowCitationViewer] = useState(false);

  const canDelete =
    userRole?.isLeader || resource.createdBy?._id === currentUserId;
  const canEdit =
    userRole?.isLeader || resource.createdBy?._id === currentUserId;

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${resource.title}"?`)) return;
    try {
      await onDelete(resource._id);
    } catch (error) {
alert("Failed to delete resource");
    }
  };

  const handleEdit = () => {
    // TODO: Implement edit functionality
    alert("Edit functionality coming soon!");
  };

  const handleOpen = () => {
    // Link type - open URL
    if (resource.resourceType === "link" && resource.url) {
      window.open(resource.url, "_blank");
      return;
    }
    
    // Dataset type - open URL if available
    if (resource.resourceType === "dataset" && resource.url) {
      window.open(resource.url, "_blank");
      return;
    }
    
    // Reference type - open citation viewer modal
    if (resource.resourceType === "reference") {
      setShowCitationViewer(true);
      return;
    }
    
    // Note type - open note viewer modal
    if (resource.resourceType === "note") {
      setShowNoteViewer(true);
      return;
    }
    
    // Code type - open code viewer modal
    if (resource.resourceType === "code") {
      const codeInfo = resource.codeContent;
      if (codeInfo?.code) {
        setShowCodeViewer(true);
        return;
      }
      // Fallback to repository if no code content
      if (codeInfo?.repository) {
        window.open(codeInfo.repository, "_blank");
        return;
      }
    }
    
    // File type - open file URL
    if (resource.fileUrl) {
      window.open(resource.fileUrl, "_blank");
      return;
    }
    
    // Fallback - try any URL
    if (resource.url) {
      window.open(resource.url, "_blank");
      return;
    }
    
    // Paper type with URL
    if (resource.resourceType === "paper" && resource.paper?.url) {
      window.open(resource.paper.url, "_blank");
      return;
    }
    
    alert("No viewable content for this resource. Try editing to add more details.");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const isListView = viewMode === "list";
  const resourceColors =
    RESOURCE_COLORS[resource.resourceType] || RESOURCE_COLORS.other;

  return (
    <motion.article
      className={`rlcard ${isListView ? "rlcard--list" : ""} ${
        resource.isPinned ? "rlcard--pinned" : ""
      }`}
      style={{
        "--card-color": resourceColors.color,
        "--card-bg": resourceColors.bg,
        "--card-shadow": resourceColors.shadow,
      }}
      whileHover={{
        y: isListView ? 0 : -4,
        boxShadow: `0 12px 24px -8px ${resourceColors.shadow}`,
      }}
      transition={{ duration: 0.2 }}
    >
      {/* Pinned Badge */}
      {resource.isPinned && (
        <div className="rlcard-pinned-badge">
          <FiMapPin size={12} />
          <span>Pinned</span>
        </div>
      )}

      {/* Icon */}
      <ResourceIcon type={resource.resourceType} />

      {/* Content */}
      <div className="rlcard-content">
        <div className="rlcard-header">
          <h3 className="rlcard-title">{resource.title}</h3>
          {(canEdit || canDelete) && (
            <div className="rlcard-actions-wrapper">
              <motion.button
                className="rlcard-more-btn"
                onClick={() => setShowMenu(!showMenu)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiMoreVertical size={16} />
              </motion.button>
              <AnimatePresence>
                {showMenu && (
                  <>
                    <div
                      className="rlcard-menu-backdrop"
                      onClick={() => setShowMenu(false)}
                    />
                    <ActionMenu
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onClose={() => setShowMenu(false)}
                    />
                  </>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {resource.description && (
          <p className="rlcard-description">{resource.description}</p>
        )}

        {/* Code Preview */}
        {resource.resourceType === "code" && resource.codeContent && (
          <div className="rlcard-code-preview">
            <div className="rlcard-code-header">
              <span className="rlcard-code-lang">
                <FiCode size={12} />
                {resource.codeContent.language || "code"}
              </span>
              {resource.codeContent.repository && (
                <a 
                  href={resource.codeContent.repository} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="rlcard-code-repo"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FiLink size={12} />
                  Repository
                </a>
              )}
            </div>
            <pre className="rlcard-code-block">
              <code>{resource.codeContent.code?.slice(0, 300)}{resource.codeContent.code?.length > 300 ? "..." : ""}</code>
            </pre>
          </div>
        )}

        {/* Note Preview */}
        {resource.resourceType === "note" && resource.noteContent && (
          <div className="rlcard-note-preview">
            <FiEdit3 size={14} />
            <p>{resource.noteContent.slice(0, 150)}{resource.noteContent.length > 150 ? "..." : ""}</p>
          </div>
        )}

        {/* Reference/Citation Preview */}
        {resource.resourceType === "reference" && resource.citation && (
          <div className="rlcard-citation-preview">
            <FiBookOpen size={14} />
            <div className="rlcard-citation-info">
              {resource.citation.authors?.length > 0 && (
                <span className="rlcard-citation-authors">
                  {resource.citation.authors.slice(0, 3).join(", ")}
                  {resource.citation.authors.length > 3 ? " et al." : ""}
                </span>
              )}
              {resource.citation.year && (
                <span className="rlcard-citation-year">({resource.citation.year})</span>
              )}
              {resource.citation.venue && (
                <span className="rlcard-citation-venue">{resource.citation.venue}</span>
              )}
              {resource.citation.doi && (
                <span className="rlcard-citation-doi">DOI: {resource.citation.doi}</span>
              )}
            </div>
          </div>
        )}

        {/* Meta Row */}
        <div className="rlcard-meta">
          <span className="rlcard-type-badge">{resource.resourceType}</span>
          <PriorityBadge priority={resource.priority} />
          {resource.folder && resource.folder !== "General" && (
            <span className="rlcard-folder">
              <FiFolder size={12} />
              {resource.folder}
            </span>
          )}
          <span className="rlcard-date">
            <FiClock size={12} />
            {formatDate(resource.createdAt)}
          </span>
        </div>

        {/* Tags */}
        {resource.tags && resource.tags.length > 0 && (
          <div className="rlcard-tags">
            {resource.tags.slice(0, 4).map((tag, idx) => (
              <span key={idx} className="rlcard-tag">
                <FiTag size={10} />
                {tag}
              </span>
            ))}
            {resource.tags.length > 4 && (
              <span className="rlcard-tag rlcard-tag--more">
                +{resource.tags.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Uploader */}
        {resource.createdBy && (
          <div className="rlcard-uploader">
            {resource.createdBy.profilePhotoUrl ? (
              <img
                src={resource.createdBy.profilePhotoUrl}
                alt={resource.createdBy.name}
                className="rlcard-avatar"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="rlcard-avatar-fallback"
              style={{
                display: resource.createdBy.profilePhotoUrl ? "none" : "flex",
              }}
            >
              {resource.createdBy.name?.charAt(0).toUpperCase() || "?"}
            </div>
            <div className="rlcard-uploader-info">
              <span className="rlcard-uploader-name">
                {resource.createdBy.name}
              </span>
              <span className="rlcard-uploader-time">
                {timeAgo(resource.createdAt)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Open Button */}
      <motion.button
        className="rlcard-open-btn"
        onClick={handleOpen}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <FiExternalLink size={16} />
        <span>Open</span>
      </motion.button>

      {/* Code Viewer Modal */}
      {resource.resourceType === "code" && resource.codeContent && (
        <CodeViewerModal
          isOpen={showCodeViewer}
          onClose={() => setShowCodeViewer(false)}
          code={resource.codeContent.code || ""}
          language={resource.codeContent.language || "javascript"}
          title={resource.title}
          repository={resource.codeContent.repository}
        />
      )}

      {/* Note Viewer Modal */}
      {resource.resourceType === "note" && (
        <NoteViewerModal
          isOpen={showNoteViewer}
          onClose={() => setShowNoteViewer(false)}
          title={resource.title}
          content={resource.noteContent || resource.description || ""}
          createdBy={resource.createdBy}
          createdAt={resource.createdAt}
          folder={resource.folder}
          tags={resource.tags}
        />
      )}

      {/* Citation Viewer Modal */}
      {resource.resourceType === "reference" && (
        <CitationViewerModal
          isOpen={showCitationViewer}
          onClose={() => setShowCitationViewer(false)}
          title={resource.title}
          citation={resource.citation}
          createdBy={resource.createdBy}
          createdAt={resource.createdAt}
        />
      )}
    </motion.article>
  );
};

export default LibraryResourceCard;
